import { useCallback } from "react";
import { Box, Divider, Flex, Text, Tooltip } from "@chakra-ui/react";

import { AiOutlineInfoCircle } from "react-icons/ai";

import { IconBackgroundContainer, InfoCardBody, TextSecondary } from "theme";

import { iconMapping } from "icons";

import Icon from "@components/Icon";
import InfoCard from "@components/InfoCard";

import {
  ANALYTICS_DASBOARD_CLICK,
  CONTRACT_ADDRESS_CLICK,
} from "@constants/analytics";
import { IIndexable } from "@customtypes/common";

// assets
import { Arrow } from "icons";

import usePageData from "@hooks/usePageData";

import { trackEvent } from "dapp-sdk-v2";

import NodeOperatorBond from "./NodeOperatorsBond";
import NodeOperatorCount from "./NodeOperatorsCount";
import BaseImage from "dapp-sdk-v2/components/Image";

interface UtilitiesCardType {
  title: string;
  description: string;
  value: string;
  key: string;
  img_key: string;
  tooltip_text?: string;
  tooltip_cta?: string;
  tooltip_cta_link?: string;
}

const ImageMapping: IIndexable = {
  dune: "/assets/images/dune.svg",
};

const ETHUtilities = ({ sectionKey }: { sectionKey: string }) => {
  const utilitiesData =
    usePageData(
      `$.page.data.right_sections.data[?(@.attributes.key=='${sectionKey}')]`
    )[0]?.attributes || {};

  const getValue = useCallback((key: string) => {
    switch (key) {
      case "node-operator-bond":
        return <NodeOperatorBond />;
      case "node-operators-count":
        return <NodeOperatorCount />;
    }
  }, []);

  const {
    contract_address,
    contract_address_cta,
    contract_address_link,
    analytics_text,
    analytics_link,
    analytics_img_key,
  } = utilitiesData?.content?.[0] || {};

  return (
    <InfoCard title={utilitiesData.title}>
      <InfoCardBody>
        <Flex gap="1rem" flexDir="column">
          {utilitiesData?.content?.[0]?.data.map((data: UtilitiesCardType) => (
            <Flex key={data.title} alignItems="center">
              <IconBackgroundContainer>
                <Box
                  w={{ base: "16px", lg: "24px" }}
                  h={{ base: "16px", lg: "24px" }}
                >
                  {data?.img_key && iconMapping(data.img_key) && (
                    <Icon
                      width="100%"
                      height="100%"
                      color="textSecondary"
                      Icon={iconMapping(data.img_key)}
                    />
                  )}
                </Box>
              </IconBackgroundContainer>
              <Flex
                ml="1rem"
                flexDirection={{ base: "row-reverse", xl: "column" }}
                justifyContent={{ base: "space-between", xl: "center" }}
                flex={1}
                lineHeight="20px"
              >
                {getValue(data.key)}
                <Flex
                  fontSize="12px"
                  fontWeight="400"
                  lineHeight="18px"
                  gap="4px"
                  color="textSecondary"
                  alignItems="center"
                >
                  {data.title}
                  {data?.tooltip_text && (
                    <Tooltip
                      label={
                        <Box pointerEvents="all">
                          <Text color="textSecondary" fontWeight="500">
                            {data?.tooltip_text}
                            {data?.tooltip_cta && (
                              <Text
                                as="a"
                                href={data?.tooltip_cta_link || ""}
                                target="_blank"
                                rel="noreferrer"
                                color="primary"
                                cursor="pointer"
                              >
                                {data?.tooltip_cta}
                              </Text>
                            )}
                          </Text>
                        </Box>
                      }
                      placement="top"
                      p="8px"
                      borderRadius="8px"
                      maxW="260px"
                      closeDelay={1500}
                      bg="tooltipBackground"
                    >
                      <Text ml="4px" color="textSecondary" fontSize="14px">
                        <AiOutlineInfoCircle />
                      </Text>
                    </Tooltip>
                  )}
                </Flex>
              </Flex>
            </Flex>
          ))}
          <Divider bg="seperator" />
          {analytics_text && analytics_link ? (
            <a
              href={analytics_link}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackEvent(ANALYTICS_DASBOARD_CLICK)}
            >
              <Flex
                flexDir="row"
                gap="16px"
                alignItems="center"
                justifyContent="center"
              >
                {analytics_img_key && (
                  <BaseImage
                    alt="analytics"
                    src={ImageMapping[analytics_img_key.toLowerCase()]}
                    width={24}
                    height={24}
                  />
                )}

                <TextSecondary
                  fontWeight="400"
                  fontSize="12px"
                  lineHeight="18px"
                >
                  {analytics_text}
                </TextSecondary>

                <Box>
                  <Icon
                    height="20px"
                    width="20px"
                    color="textPrimary"
                    Icon={Arrow}
                  />
                </Box>
              </Flex>
            </a>
          ) : null}

          {contract_address_link && (
            <a
              href={contract_address_link}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackEvent(CONTRACT_ADDRESS_CLICK)}
            >
              <Flex
                flexDir="row"
                gap="6px"
                alignItems="center"
                justifyContent="center"
                bg="iconBackground"
                borderRadius="6px"
                fontSize="12px"
                fontWeight="500"
                lineHeight="18px"
                p="4px 6px"
              >
                <TextSecondary>{contract_address_cta}</TextSecondary>
                {contract_address && (
                  <TextSecondary>
                    {`${contract_address.substring(
                      0,
                      3
                    )}...${contract_address.substring(
                      contract_address.length - 3
                    )}`}
                  </TextSecondary>
                )}

                <Box>
                  <Icon
                    height="20px"
                    width="20px"
                    color="textPrimary"
                    Icon={Arrow}
                  />
                </Box>
              </Flex>
            </a>
          )}
        </Flex>
      </InfoCardBody>
    </InfoCard>
  );
};

export default ETHUtilities;
